<script setup lang="ts">
import {computed, reactive, ref} from "vue";
import {Loading, Search} from "@element-plus/icons-vue";
import CourseDiscussItem from "components/course/discuss/CourseDiscussItem.vue";
import {CourseDiscuss, SimpleSection} from "@/types";
import {CourseService} from "@/api/api.ts";
import {ElMessageBox, ElNotification} from "element-plus";
import DiscussReplyDialog from "components/course/discuss/DiscussReplyDialog.vue";

const discussReplyDialog = ref<InstanceType<typeof DiscussReplyDialog>>()
const replyContent = ref("")
const loadingFirst = ref(false)
const loading = ref(false)
const noMore = ref(false)
const type = ref(0)
const courseId = ref()
const sectionId = ref()
const way = ref(1)
const content = ref("")
const username = ref("")
const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0
})
const disabled = computed(() => loading.value || noMore.value)

const loadingRemote = ref(false)
const currentParentItem = ref<CourseDiscuss>()
const courseDiscussList = ref<CourseDiscuss[]>([])

const courseList = ref<SimpleSection[]>([])
const sectionList = ref<SimpleSection[]>([])

const onReply = async (item: CourseDiscuss) => {
  currentParentItem.value = item
  discussReplyDialog.value?.show()
}

const onDel = async (item: CourseDiscuss) => {
  currentParentItem.value = item
  try {
    await ElMessageBox.confirm(
        '确认删除该讨论内容吗',
        '注意',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
    )
    const res = await CourseService.delDiscussReply({
      id: currentParentItem.value.id,
    })
    if (res.data.code === 200) {
      ElNotification.success({
        title: '删除成功',
        message: res.data.msg,
        type: 'error'
      })
      onSearch()
    } else {
      ElNotification.error({
        title: '删除失败',
        message: res.data.msg,
        type: 'error'
      })
    }
  } catch (error) {
    console.warn(error)
  }
}

const newSearch = () => {
  pagination.current = 1
  noMore.value = false
  way.value = 1
  content.value = ""
  username.value = ""
  getCourseDiscussList()
}

const getCourseDiscussList = async () => {
  try {
    loading.value = true
    if (pagination.current === 1) {
      loadingFirst.value = true
    }
    const res = await CourseService.getCourseDiscussList({
      page: pagination.current,
      pageSize: pagination.pageSize,
      type: type.value,
      course_id: courseId.value,
      section_id: sectionId.value,
      way: way.value,
      content: content.value,
      user_name: username.value
    })
    if (res.data.code === 200) {
      noMore.value = !(res.data.data?.list && res.data.data.list.length > 0);
      if (res.data.data?.page === 1) {
        courseDiscussList.value = res.data.data.list
      } else {
        if (res.data.data?.list && res.data.data?.list.length > 0) {
          courseDiscussList.value.push(...res.data.data.list)
        }
      }
      pagination.current = res.data.data?.page || 1
      pagination.total = res.data.data?.count || 0
    } else {
      ElNotification.error({
        title: '失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (e) {
    console.warn(e)
  } finally {
    loadingFirst.value = false
    loading.value = false
  }
}

const onSubmit = (content: string) => {
  replyContent.value = content
  replyDiscuss()
}

const replyDiscuss = async () => {
  if (!replyContent.value) {
    return ElNotification.warning({
      title: "注意",
      message: "回复内容不能为空",
      type: "error",
    })
  }
  const res = await CourseService.replyDiscuss({
    id: currentParentItem.value?.id || 0,
    content: replyContent.value,
  })
  if (res.data.code === 200) {
    ElNotification.success({
      title: "回复成功",
      message: res.data.msg,
      type: "success",
    })
    discussReplyDialog.value?.hide()
    if (currentParentItem.value) {
      currentParentItem.value.comment_num++
    }
  } else {
    ElNotification.error({
      title: "回复失败",
      message: res.data.msg,
      type: "error",
    })
  }
}

const remoteMethod = (query: string) => {
  if (query) {
    getCourseList(query)
  } else {
    courseList.value = []
  }
}

const onCourseTypeChange = () => {
  courseId.value = undefined
  courseList.value = []
  sectionId.value = undefined
  sectionList.value = []
  newSearch()
}

const getCourseList = async (title: string) => {
  try {
    loadingRemote.value = true
    const res = await CourseService.getCourseSectionList({
      type: type.value,
      title: title,
    })
    if (res.data.code === 200) {
      courseList.value = [{id: 0, title: "全部课程"}, ...res.data.data!!]
    } else {
      ElNotification.error({
        title: '获取课程列表失败',
        message: res.data.msg,
        type: 'error'
      })
    }
  } catch (err) {
    console.warn(err)
  } finally {
    loadingRemote.value = false
  }
}

const onCourseChange = async () => {
  sectionId.value = undefined
  sectionList.value = []
  onSearch()
  try {
    const res = await CourseService.getCourseSectionList({
      course_id: courseId.value,
    })
    if (res.data.code === 200) {
      sectionList.value = res.data.data!!
    } else {
      ElNotification.error({
        title: '获取章节列表失败',
        message: res.data.msg,
        type: 'error'
      })
    }
  } catch (err) {
    console.warn(err)
  }
}

const onSectionChange = () => {
  onSearch()
}

const onWayChange = (_way: number) => {
  way.value = _way
  onSearch()
}

const onSearch = () => {
  pagination.current = 1
  noMore.value = false
  getCourseDiscussList()
}

const load = () => {
  // 加载更多
  if (!noMore.value) {
    pagination.current++
    getCourseDiscussList()
  }
}

getCourseDiscussList()
</script>

<template>
  <div class="mx-3rem mt-2.25rem bg-white rounded-md shadow-md pt-1rem">
    <el-tabs v-model="type" @tab-change="onCourseTypeChange" class="px-2rem tab-font">
      <el-tab-pane label="全部讨论" :name="0"></el-tab-pane>
      <el-tab-pane label="直播课讨论" :name="1"></el-tab-pane>
      <el-tab-pane label="录播课讨论" :name="2"></el-tab-pane>
    </el-tabs>
    <div class="relative">
      <div class="flex items-center mt-1rem gap-1rem ml-2rem flex-wrap">
        <el-input v-model="content" style="width: 12rem" :suffix-icon="Search" placeholder="关键字搜索"
                  @change="onSearch"></el-input>
        <el-input v-model="username" style="width: 12rem;" :suffix-icon="Search" placeholder="用户名搜索"
                  @change="onSearch"></el-input>
        <el-select
            v-model="courseId"
            style="width: 12rem"
            filterable remote remote-show-suffix :remote-method="remoteMethod"
            placeholder="请输入课程标题" @change="onCourseChange">
          <el-option
              v-for="item in courseList"
              :key="item.id"
              :label="item.title"
              :value="item.id">
          </el-option>
        </el-select>
        <el-select style="width: 12rem" v-model="sectionId" placeholder="请选择章节"
                   @change="onSectionChange">
          <el-option
              v-for="item in sectionList"
              :key="item.id"
              :label="item.title"
              :value="item.id">
          </el-option>
        </el-select>
        <div class="flex items-center mt-.5rem gap-.5rem absolute right-2rem">
          <div class="cursor-pointer" :class="way === 1 ? `text-#FF6600CC` : `text-#bfbfbf`" @click="onWayChange(1)">
            最新发布
          </div>
          <div class="w-0 border-l-0 border-r h-16px border-#bfbfbf border-dashed"></div>
          <div class="cursor-pointer" :class="way === 2 ? `text-#FF6600CC` : `text-#bfbfbf`" @click="onWayChange(2)">
            点赞最多
          </div>
          <div class="w-0 border-l-0 border-r h-16px border-#bfbfbf border-dashed"></div>
          <div class="cursor-pointer" :class="way === 3 ? `text-#FF6600CC` : `text-#bfbfbf`" @click="onWayChange(3)">
            回复最多
          </div>
        </div>
      </div>
      <div
          v-loading="loadingFirst"
          v-infinite-scroll="load"
          :infinite-scroll-disabled="disabled">
        <template v-for="item in courseDiscussList" :key="item.id">
          <course-discuss-item
              :course-discuss="item"
              @on-reply="onReply"
              @on-del="onDel">
          </course-discuss-item>
        </template>
        <el-empty v-if="courseDiscussList.length === 0" class="mt-2.5rem" description="没有讨论~"></el-empty>
        <div class="flex justify-center" v-if="loading">
          <p class="mx-auto text-#939393">
            <el-icon>
              <Loading class="animate-spin"></Loading>
            </el-icon>
            加载中...
          </p>
        </div>
        <div class="flex justify-center" v-if="noMore && courseDiscussList.length > 0">
          <p class="mx-auto text-#939393">没有更多了...</p>
        </div>
      </div>
    </div>
  </div>


  <discuss-reply-dialog ref="discussReplyDialog" @on-submit="onSubmit"/>
</template>

<style scoped>
.tab-font {
  --el-font-size-base: 18px;
}
</style>
